import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";

import { IAllVehicleResponse, ISingleVehicleResponse, IVehicle } from "../models/Vehicle";
import { DefaultResponseWithData } from "../models/system";

export default function useVehiclesMutations() {
  const { get, post, put, patch, del } = useApiHelper();
  const queryClient = useQueryClient();

  const vrmLookup = useMutation(
    (vrm: string) => get<ISingleVehicleResponse>(`/vehicle/lookup/${vrm}`),
    {
      onSuccess: () => {

      },
    }
  );

  const createMisc = useMutation(
    (vehicle: any) => post<ISingleVehicleResponse>("/vehicle/misc", vehicle),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["vehicles"]);
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead"]);
        queryClient.setQueryData(["vehicles", data.data.id], data);
      },
      onError: (error) => {
        console.error("Error creating miscellaneous vehicle:", error);
      },
    }
  );

  const update = useMutation(
    (vehicle: IVehicle) => put(`/vehicle/${vehicle.id}`, vehicle),
    {
      onMutate: (vehicle: IVehicle) => {
        queryClient.cancelQueries(["vehicles"]);
        const previousVehicle = queryClient.getQueryData<DefaultResponseWithData<IVehicle>>(["vehicles", vehicle.id]);

        queryClient.setQueryData(["vehicles", vehicle.id], {
          success: true,
          message: "Optimistically updated",
          data: vehicle,
        });

        return { previousVehicle, vehicle };
      },
      onError: (err, vehicle, context) => {
        queryClient.setQueryData(
          ['vehicles', context?.vehicle.id],
          context?.previousVehicle,
        )
      },
      onSettled: (_data, _error, _vehicle) => {
        queryClient.invalidateQueries(["vehicles"]);
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead"]);
        queryClient.invalidateQueries(["job"]);
      },
    }
  );

  const updateDetails = useMutation(
    (data: { vehicleId: string; details: Partial<IVehicle> }) => put(`/vehicle/${data.vehicleId}/details`, data.details),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["vehicles"]);
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead"]);
        // Details can change the purchase VAT flag, which drives parts' resolved treatment.
        queryClient.invalidateQueries(["part-vat-resolution"]);
        queryClient.invalidateQueries(["omniSearchVat"]);
      },
    }
  );

  const bulkUpdate = useMutation(
    (body: any) => post(`/vehicle/bulk`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["vehicles"]);
      },
    }
  );

  const vrmRefresh = useMutation(
    (vehicle: IVehicle) => post(`/vehicle/${vehicle.id}/refresh`, { "vrm": vehicle.vrm }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["vehicles"]);
        queryClient.invalidateQueries(["lead"]);
      },
    }
  );

  const enginesWorldwideRefresh = useMutation(
    (vehicleId: string) => post<undefined, ISingleVehicleResponse>(`/vehicle/${vehicleId}/engines-worldwide/refresh`, undefined),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["vehicles"]);
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead"]);
        queryClient.setQueryData(["vehicles", data.data.id], data);
      },
    }
  );

  const updateNotes = useMutation(
    (data: { vehicleId: string; notes: string | null }) =>
      patch(`/note/vehicle`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["vehicles"]);
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead"]);
      },
    }
  );

  const bulkMarkCollected = useMutation(
    (body: any) => post(`/vehicle/bulk/mark/collected`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["vehicles"]);
        queryClient.invalidateQueries(["vehiclePagedTable"]);
      },
    }
  );

  const patchVehicle = useMutation(
    (data: { vehicleId: string; patch: object }) => patch(`/vehicle/${data.vehicleId}`, data.patch),
    {
      onSettled: (_data, _error, variables) => {
        queryClient.invalidateQueries(["vehicles"]);
        queryClient.invalidateQueries(["vehicles", variables.vehicleId, "workflow"]);
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead"]);
        queryClient.invalidateQueries(["planner-awaiting"]);
        queryClient.invalidateQueries(["planner-allocated"]);
        queryClient.invalidateQueries(["production-dashboard-overview"]);
        queryClient.invalidateQueries(["pagedTable", "/vehicle"]);
      },
    }
  );

  const softDelete = useMutation(
    (vehicleId: string) => del(`/vehicle/${vehicleId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["vehicles"]);
        queryClient.invalidateQueries(["vehiclePagedTable"]);
      },
    }
  );

  return {
    vrmLookup,
    createMisc,
    update,
    updateDetails,
    bulkUpdate,
    vrmRefresh,
    enginesWorldwideRefresh,
    updateNotes,
    bulkMarkCollected,
    patchVehicle,
    softDelete,
  };
}
